/* jshint esversion: 6 */

/**
 * HintSelectLevelValidator class is responsible for validating the selection level of hints.
 * It uses dependency injection to get necessary services and performs various checks to ensure
 * the hint levels are correctly set.
 */
class HintSelectLevelValidator {
    /**
     * Constructor for HintSelectLevelValidator.
     * @param {Object} model - The model object to be validated.
     * @param {Object} $injector - AngularJS injector service.
     */
    constructor(model, $injector) {
        this.model = model;
        this.$injector = $injector;

        this._TaskDataService = null;
        this._translate = null;
        this._fetchContext = null;
        this._messageTranslateString = 'CMS.FACTORIES.MODELS.VALIDATORS.LOGIC.HINT-SELECT-LEVEL';
        this._message = null;
        this._forced_check = false;
    }

    /**
     * Get the TaskDataService instance.
     * @returns {Object} TaskDataService instance.
     */
    getTaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Get the $translate service instance.
     * @returns {Object} $translate service instance.
     */
    getTranslateService() {
        if (!this._translate) {
            this._translate = this.$injector.get('$translate');
        }
        return this._translate;
    }

    /**
     * Get the fetchContext instance.
     * @returns {Object} fetchContext instance.
     */
    getFetchContext() {
        if (!this._fetchContext) {
            this._fetchContext = this.getTaskDataService().fetchContext();
        }
        return this._fetchContext;
    }

    /**
     * Check if there exists a sub-level for the given objects and preferences.
     * @param {Array} objects - Array of object UUIDs.
     * @param {Object} object_preferences - Preferences of the object.
     * @returns {boolean} True if a sub-level exists, false otherwise.
     */
    _existsSubLevel(objects, object_preferences) {
        let result = false;

        if (objects.find(uuid => {
                if (!angular.isUUID(uuid)) return false;

                let content_object = this.getFetchContext().read(uuid);

                let preferences;
                if (angular.isFunction(content_object.getPreferences)) {
                    preferences = content_object.getPreferences();
                } else {
                    preferences = content_object.preferences;
                }

                return preferences.level === (object_preferences.level - 1);
            })) {
            result = true;
        }

        return result;
    }

    /**
     * Get the parent container of the model.
     * @param {Object} model - The model object.
     * @returns {Object|null} The parent container or null if not found.
     */
    _getParentContainer(model) {
        let parents = model.getParents();

        let parent_container = null;
        let parent_containers = parents.filter(item => item.getType() === 'container');
        for (let container of parent_containers) {
            let container_instance = this.getFetchContext().read(container.getUUID());
            let objects;
            if (container_instance.isShowTabs()) {
                let sub_containers = container_instance.getTabs();
                for (let sub_container of sub_containers) {
                    let sub_container_instance = this.getFetchContext().read(sub_container.content);
                    objects = sub_container_instance.getObjects();
                }
            } else if (container_instance.isShowCarousels()) {
                let sub_containers = container_instance.getCarousels();
                for (let sub_container of sub_containers) {
                    let sub_container_instance = this.getFetchContext().read(sub_container.content);
                    objects = sub_container_instance.getObjects();
                }
            } else if (container_instance.isShowIaaContainer()) {
                let sub_containers = container_instance.getIaaContainer();
                for (let sub_container of sub_containers) {
                    let sub_container_instance = this.getFetchContext().read(sub_container.content);
                    objects = sub_container_instance.getObjects();
                }
            } else {
                objects = container_instance.getObjects();
            }

            if (objects && objects.includes(model.getUUID())) {
                parent_container = container_instance;
            }
        }

        return parent_container;
    }

    /**
     * Validate the model.
     * @param {Object} from - The source object initiating the validation.
     * @returns {Object} Validation result.
     */
    validate(from) {
        let result = {};
        let error = false;

        if (angular.isFunction(this.model.getObjectType) && this.model.getObjectType() === 'hint') {
            let preferences;
            if (angular.isFunction(this.model.getPreferences)) {
                preferences = this.model.getPreferences();
            } else {
                preferences = this.model.preferences;
            }
            if (preferences.level) {
                let parent_container = this._getParentContainer(this.model);

                if (parent_container && parent_container.data && parent_container.data.objects.length > 0) {
                    let objects = parent_container.data.objects;

                    for (let uuid of objects) {
                        if (!angular.isUUID(uuid)) continue;

                        let content_object = this.getFetchContext().read(uuid);

                        if (content_object.getObjectType() !== 'hint') continue;

                        let content_object_preferences;
                        if (angular.isFunction(content_object.getPreferences)) {
                            content_object_preferences = content_object.getPreferences();
                        } else {
                            content_object_preferences = content_object.preferences;
                        }

                        if (content_object_preferences.level > 1) {
                            if (!this._existsSubLevel(objects, content_object_preferences)) {
                                error = true;
                            }
                        }
                    }
                }
            }
        }

        result = {
            'onSheetBlur': false,
            'onlyBanner': false,
            'validator': this.getValidatorName(),
            'modelUUID': this.model.getUUID(),
            'error': error,
            'status': error ? 'DANGER' : 'SUCCESS', // WARNING | DANGER | SUCCESS
            'message': this.getErrorMessage(),
            'message_template': false,
            'callbacks': [
                'validationDisplayObjectHeader-' + this.model.getUUID()
            ]
        };

        return result;
    }

    /**
     * Get the error message for the validation.
     * @returns {string} Error message.
     */
    getErrorMessage() {
        let questionHeadline = this.model.getQuestionHeadline();
        this._message = this.getTranslateService().instant(this._messageTranslateString, {
            s: questionHeadline ? questionHeadline : ''
        });

        return this._message;
    }

    /**
     * Check if the forced check is enabled.
     * @returns {boolean} True if forced check is enabled, false otherwise.
     */
    forcedCheck() {
        return this._forced_check;
    }

    /**
     * Get the name of the validator.
     * @returns {string} Validator name.
     */
    getValidatorName() {
        return 'HintSelectLevelValidator';
    }
}

export default HintSelectLevelValidator;